import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "@/lib/is-tauri";
import { isMacPlatform } from "@/lib/os-platform";

export type SafariAutomationStatus =
  | "granted"
  | "consent_required"
  | "denied"
  | "not_running"
  | "unavailable";

export type SafariConnectionStatus = {
  automation: { status: SafariAutomationStatus; code: number | null };
  javascript: "not_checked" | "ready" | "unavailable";
  reason:
    | "not_checked"
    | "automation_required"
    | "safari_not_running"
    | "no_page"
    | "probe_unavailable"
    | "disconnected"
    | "ready";
};

export function safariConnectionApplies(): boolean {
  return isTauri() && isMacPlatform();
}

export function parseSafariConnectionStatus(
  value: unknown,
): SafariConnectionStatus {
  if (!value || typeof value !== "object")
    throw new Error("Invalid Safari status");
  const status = value as SafariConnectionStatus;
  if (
    !status.automation ||
    ![
      "granted",
      "consent_required",
      "denied",
      "not_running",
      "unavailable",
    ].includes(status.automation.status) ||
    !(
      status.automation.code === null ||
      Number.isInteger(status.automation.code)
    ) ||
    !["not_checked", "ready", "unavailable"].includes(status.javascript) ||
    ![
      "not_checked",
      "automation_required",
      "safari_not_running",
      "no_page",
      "probe_unavailable",
      "disconnected",
      "ready",
    ].includes(status.reason) ||
    ((status.javascript === "ready" || status.reason === "ready") &&
      !(
        status.automation.status === "granted" &&
        status.javascript === "ready" &&
        status.reason === "ready"
      ))
  ) {
    throw new Error("Invalid Safari status");
  }
  return status;
}

async function readStatus(command: string): Promise<SafariConnectionStatus> {
  if (!safariConnectionApplies())
    throw new Error("Safari connection unavailable");
  return parseSafariConnectionStatus(await invoke<unknown>(command));
}

async function permissionStatus(
  command: string,
): Promise<SafariConnectionStatus> {
  const status = await readStatus(command);
  return {
    ...status,
    javascript: "not_checked",
    reason: status.reason === "ready" ? "not_checked" : status.reason,
  };
}

/** Passive permission read. A previous page check never carries into this read. */
export function getSafariConnectionStatus(): Promise<SafariConnectionStatus> {
  return permissionStatus("get_safari_connection_status");
}

export function checkSafariConnection(): Promise<SafariConnectionStatus> {
  return readStatus("check_safari_connection");
}
